import { FormEvent, useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { api } from '../api/client';
import { OrgLayout } from '../components/OrgLayout';
import { Modal } from '../components/Modal';
import { useToast } from '../components/Toast';
import type {
  DepartmentDto, ExamPattern, GeneratedPaper, QuestionType, Syllabus,
  UpsertExamPatternSectionRequest,
} from '../types/models';

const QTYPES: { value: QuestionType; label: string }[] = [
  { value: 'MultipleChoiceSingle', label: 'MCQ — single answer' },
  { value: 'MultipleChoiceMulti', label: 'MCQ — multiple answers' },
  { value: 'Descriptive', label: 'Descriptive / long answer' },
];
const qtypeLabel = (t: QuestionType) => QTYPES.find(x => x.value === t)?.label ?? t;

type SectionRow = UpsertExamPatternSectionRequest;

const emptySection = (): SectionRow => ({
  title: '', instructions: '', questionType: 'Descriptive',
  questionCount: 5, requiredAnswers: 0, marksPerQuestion: 10, isEitherOr: false,
});

function sectionMarks(s: SectionRow): number {
  const answered = s.requiredAnswers <= 0 ? s.questionCount : Math.min(s.requiredAnswers, s.questionCount);
  return Math.max(0, answered) * Math.max(0, s.marksPerQuestion);
}

export function SyllabusPage() {
  const { orgId = '' } = useParams();
  const nav = useNavigate();
  const toast = useToast();

  const [depts, setDepts] = useState<DepartmentDto[]>([]);
  const [deptId, setDeptId] = useState('');
  const [patterns, setPatterns] = useState<ExamPattern[] | null>(null);
  const [syllabi, setSyllabi] = useState<Syllabus[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  // Pattern editor modal.
  const [showPattern, setShowPattern] = useState(false);
  const [editPatternId, setEditPatternId] = useState<string | null>(null);
  const [pName, setPName] = useState('');
  const [pDuration, setPDuration] = useState(180);
  const [pSections, setPSections] = useState<SectionRow[]>([emptySection()]);
  const [pBusy, setPBusy] = useState(false);
  const [pError, setPError] = useState<string | null>(null);

  // Upload form.
  const [upTitle, setUpTitle] = useState('');
  const [upCourse, setUpCourse] = useState('');
  const [upSemester, setUpSemester] = useState(1);
  const [upTextbook, setUpTextbook] = useState('');
  const [upFile, setUpFile] = useState<File | null>(null);
  const [upBusy, setUpBusy] = useState(false);

  // Generation + preview.
  const [genPatternId, setGenPatternId] = useState('');
  const [genUnits, setGenUnits] = useState('');
  const [genBusy, setGenBusy] = useState<string | null>(null); // syllabusId being generated
  const [preview, setPreview] = useState<{ syllabusId: string; paper: GeneratedPaper } | null>(null);
  const [accepting, setAccepting] = useState(false);

  useEffect(() => {
    api.listDepartments(orgId).then(setDepts).catch(e => setError(e?.message ?? 'Failed to load departments.'));
  }, [orgId]);

  useEffect(() => {
    if (!deptId) { setPatterns(null); setSyllabi(null); return; }
    refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [deptId]);

  async function refresh() {
    try {
      const [p, s] = await Promise.all([api.listExamPatterns(deptId), api.listSyllabi(deptId)]);
      setPatterns(p);
      setSyllabi(s);
      // Default the generation pattern to the first available.
      setGenPatternId(prev => (p.some(x => x.id === prev) ? prev : (p[0]?.id ?? '')));
    } catch (e: any) {
      setError(e?.message ?? 'Failed to load.');
    }
  }

  const pTotal = useMemo(() => pSections.reduce((sum, s) => sum + sectionMarks(s), 0), [pSections]);

  function openCreatePattern() {
    setPError(null); setEditPatternId(null);
    setPName(''); setPDuration(180); setPSections([emptySection()]);
    setShowPattern(true);
  }

  function openEditPattern(p: ExamPattern) {
    setPError(null); setEditPatternId(p.id);
    setPName(p.name); setPDuration(p.durationMinutes);
    setPSections(p.sections.map(s => ({
      title: s.title, instructions: s.instructions ?? '', questionType: s.questionType,
      questionCount: s.questionCount, requiredAnswers: s.requiredAnswers, marksPerQuestion: s.marksPerQuestion,
      isEitherOr: s.isEitherOr,
    })));
    setShowPattern(true);
  }

  function setSection(idx: number, patch: Partial<SectionRow>) {
    setPSections(rows => rows.map((r, i) => (i === idx ? { ...r, ...patch } : r)));
  }

  async function savePattern(e: FormEvent) {
    e.preventDefault();
    setPError(null);
    if (pSections.length === 0) { setPError('Add at least one section.'); return; }
    setPBusy(true);
    try {
      const req = {
        departmentId: deptId,
        name: pName.trim(),
        durationMinutes: pDuration,
        sections: pSections.map(s => ({
          title: s.title.trim(),
          instructions: s.instructions?.trim() ? s.instructions.trim() : null,
          questionType: s.questionType,
          questionCount: s.questionCount,
          requiredAnswers: s.requiredAnswers,
          marksPerQuestion: s.marksPerQuestion,
          isEitherOr: s.isEitherOr,
        })),
      };
      if (editPatternId) await api.updateExamPattern(editPatternId, req);
      else await api.createExamPattern(req);
      setShowPattern(false);
      await refresh();
      toast.success(editPatternId ? 'Pattern updated.' : 'Pattern created.');
    } catch (e: any) {
      setPError(e?.message ?? 'Failed to save pattern.');
    } finally {
      setPBusy(false);
    }
  }

  async function removePattern(p: ExamPattern) {
    if (!confirm(`Delete pattern “${p.name}”?`)) return;
    try {
      await api.deleteExamPattern(p.id);
      await refresh();
      toast.success('Pattern deleted.');
    } catch (e: any) {
      toast.error(e?.message ?? 'Failed to delete pattern.');
    }
  }

  async function upload(e: FormEvent) {
    e.preventDefault();
    if (!upFile) { toast.error('Choose a PDF file.'); return; }
    setUpBusy(true);
    try {
      await api.uploadSyllabus(deptId, upTitle.trim(), upCourse.trim(), upSemester, upTextbook.trim(), upFile.name, upFile);
      setUpTitle(''); setUpCourse(''); setUpTextbook(''); setUpFile(null);
      // Reset the file input element.
      const input = document.getElementById('syllabus-file') as HTMLInputElement | null;
      if (input) input.value = '';
      await refresh();
      toast.success('Syllabus uploaded.');
    } catch (e: any) {
      toast.error(e?.message ?? 'Upload failed.');
    } finally {
      setUpBusy(false);
    }
  }

  async function removeSyllabus(s: Syllabus) {
    if (!confirm(`Delete syllabus “${s.title}”?`)) return;
    try {
      await api.deleteSyllabus(s.id);
      await refresh();
      toast.success('Syllabus deleted.');
    } catch (e: any) {
      toast.error(e?.message ?? 'Failed to delete syllabus.');
    }
  }

  async function generate(s: Syllabus) {
    if (!genPatternId) { toast.error('Choose an exam pattern first.'); return; }
    setGenBusy(s.id);
    try {
      const paper = await api.generatePaper(s.id, genPatternId, genUnits.trim());
      setPreview({ syllabusId: s.id, paper });
    } catch (e: any) {
      toast.error(e?.message ?? 'Generation failed.');
    } finally {
      setGenBusy(null);
    }
  }

  async function accept() {
    if (!preview) return;
    setAccepting(true);
    try {
      const examId = await api.acceptGeneratedPaper(preview.syllabusId, preview.paper);
      toast.success('Draft exam created.');
      nav(`/org/${orgId}/exams/${examId}`);
    } catch (e: any) {
      toast.error(e?.message ?? 'Failed to create draft exam.');
    } finally {
      setAccepting(false);
    }
  }

  return (
    <OrgLayout>
      <div className="row" style={{ justifyContent: 'space-between' }}>
        <h1 style={{ margin: 0 }}>AI Question Paper</h1>
      </div>
      <p className="muted" style={{ marginTop: 4 }}>
        Upload a subject syllabus and generate an end-semester question paper that follows the
        department's exam pattern. Preview and edit it, then accept it into a draft exam.
      </p>

      {error && <div className="error" style={{ margin: '12px 0' }}>{error}</div>}

      <div className="card" style={{ marginTop: 12 }}>
        <label style={{ maxWidth: 420 }}>
          <div className="muted">Department</div>
          <select value={deptId} onChange={e => setDeptId(e.target.value)}>
            <option value="">Select a department…</option>
            {depts.map(d => <option key={d.id} value={d.id}>{d.code} — {d.name}</option>)}
          </select>
        </label>
        {depts.length === 0 && (
          <div className="muted" style={{ marginTop: 8 }}>
            No departments yet — create one under <Link to={`/org/${orgId}/departments`}>Department</Link> first.
          </div>
        )}
      </div>

      {deptId && (
        <>
          {/* ---- Exam patterns ---- */}
          <div className="card" style={{ marginTop: 16 }}>
            <div className="row" style={{ justifyContent: 'space-between', alignItems: 'center' }}>
              <h2 style={{ margin: 0, fontSize: 18 }}>Exam patterns</h2>
              <button className="primary" onClick={openCreatePattern}>+ Add pattern</button>
            </div>
            <div style={{ marginTop: 12, overflowX: 'auto' }}>
              <table className="data-table">
                <thead>
                  <tr>
                    <th>Name</th><th>Sections</th><th>Duration</th><th>Total Marks</th>
                    <th style={{ width: 160 }}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {!patterns && <tr><td colSpan={5} className="muted">Loading…</td></tr>}
                  {patterns && patterns.length === 0 && (
                    <tr><td colSpan={5} className="muted">No patterns yet. Add one to describe the paper structure.</td></tr>
                  )}
                  {patterns?.map(p => (
                    <tr key={p.id}>
                      <td style={{ fontWeight: 600 }}>{p.name}</td>
                      <td>{p.sections.length}</td>
                      <td>{p.durationMinutes} min</td>
                      <td>{p.totalMarks}</td>
                      <td>
                        <button className="link-btn" onClick={() => openEditPattern(p)}>Edit</button>
                        <button className="link-btn" style={{ color: 'var(--danger)' }} onClick={() => removePattern(p)}>Delete</button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* ---- Syllabi ---- */}
          <div className="card" style={{ marginTop: 16 }}>
            <h2 style={{ margin: 0, fontSize: 18 }}>Syllabi</h2>
            <form onSubmit={upload} className="row" style={{ gap: 12, flexWrap: 'wrap', alignItems: 'flex-end', marginTop: 12 }}>
              <label style={{ flex: '1 1 200px' }}>
                <div className="muted">Subject title *</div>
                <input value={upTitle} required maxLength={300}
                       onChange={e => setUpTitle(e.target.value)} placeholder="Data Structures" />
              </label>
              <label style={{ flex: '0 0 150px' }}>
                <div className="muted">Course code</div>
                <input value={upCourse} maxLength={64}
                       onChange={e => setUpCourse(e.target.value)} placeholder="CS8391" />
              </label>
              <label style={{ flex: '0 0 110px' }}>
                <div className="muted">Semester</div>
                <input type="number" min={1} max={12} value={upSemester}
                       onChange={e => setUpSemester(parseInt(e.target.value) || 1)} />
              </label>
              <label style={{ flex: '1 1 240px' }}>
                <div className="muted">Textbook / reference (optional)</div>
                <input value={upTextbook} maxLength={1000}
                       onChange={e => setUpTextbook(e.target.value)}
                       placeholder="e.g. Cormen, Introduction to Algorithms" />
              </label>
              <label style={{ flex: '1 1 240px' }}>
                <div className="muted">Syllabus PDF *</div>
                <input id="syllabus-file" type="file" accept="application/pdf"
                       onChange={e => setUpFile(e.target.files?.[0] ?? null)} />
              </label>
              <button type="submit" className="primary" disabled={upBusy || !upFile || !upTitle.trim()}>
                {upBusy ? 'Uploading…' : 'Upload'}
              </button>
            </form>

            <div className="row" style={{ gap: 12, flexWrap: 'wrap', alignItems: 'flex-end', marginTop: 16 }}>
              <label style={{ flex: '1 1 280px' }}>
                <div className="muted">Generate using pattern</div>
                <select value={genPatternId} onChange={e => setGenPatternId(e.target.value)} disabled={!patterns?.length}>
                  {!patterns?.length && <option value="">Create a pattern first</option>}
                  {patterns?.map(p => <option key={p.id} value={p.id}>{p.name} ({p.totalMarks} marks)</option>)}
                </select>
              </label>
              <label style={{ flex: '1 1 100%' }}>
                <div className="muted">Units / topics to cover (blank = whole syllabus)</div>
                <textarea value={genUnits} onChange={e => setGenUnits(e.target.value)} rows={2}
                          placeholder={'e.g. "Units 1 and 2, and the first half of Unit 3"  •  "Units I–III"  •  "Unit 3: only Hashing and Heaps"'} />
                <div className="muted" style={{ fontSize: 12, marginTop: 4 }}>
                  Describe the scope in plain words. You can name whole units, a range, a partial unit
                  ("first half of Unit 3"), or specific topics — the AI uses the syllabus's unit
                  structure to stay within what you describe.
                </div>
              </label>
            </div>

            <div style={{ marginTop: 12, overflowX: 'auto' }}>
              <table className="data-table">
                <thead>
                  <tr>
                    <th>Subject</th><th>Course</th><th>Sem</th><th>File</th><th>Uploaded</th>
                    <th style={{ width: 240 }}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {!syllabi && <tr><td colSpan={6} className="muted">Loading…</td></tr>}
                  {syllabi && syllabi.length === 0 && (
                    <tr><td colSpan={6} className="muted">No syllabi uploaded yet.</td></tr>
                  )}
                  {syllabi?.map(s => (
                    <tr key={s.id}>
                      <td style={{ fontWeight: 600 }}>{s.title}</td>
                      <td className="slug">{s.courseCode || '—'}</td>
                      <td>{s.semester > 0 ? s.semester : '—'}</td>
                      <td className="muted">{s.fileName}</td>
                      <td className="muted">{new Date(s.createdAt).toLocaleString()}</td>
                      <td>
                        <button className="link-btn" disabled={!genPatternId || genBusy === s.id}
                                onClick={() => generate(s)}>
                          {genBusy === s.id ? 'Generating…' : 'Generate paper'}
                        </button>
                        <button className="link-btn" style={{ color: 'var(--danger)' }}
                                onClick={() => removeSyllabus(s)}>Delete</button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}

      {/* ---- Pattern editor modal ---- */}
      {showPattern && (
        <Modal title={editPatternId ? 'Edit exam pattern' : 'Create exam pattern'} onClose={() => setShowPattern(false)}>
          <form onSubmit={savePattern} className="col" style={{ marginTop: 16 }}>
            <div className="row" style={{ gap: 12 }}>
              <label style={{ flex: 1 }}>
                <div className="muted">Pattern name *</div>
                <input value={pName} required maxLength={200}
                       onChange={e => setPName(e.target.value)} placeholder="End Semester" />
              </label>
              <label style={{ flex: '0 0 160px' }}>
                <div className="muted">Duration (min) *</div>
                <input type="number" min={1} value={pDuration}
                       onChange={e => setPDuration(parseInt(e.target.value) || 0)} />
              </label>
            </div>

            <div className="muted" style={{ marginTop: 8, fontWeight: 600 }}>Sections</div>
            {pSections.map((s, idx) => (
              <div key={idx} className="card" style={{ padding: 12, marginTop: 8 }}>
                <div className="row" style={{ gap: 12, flexWrap: 'wrap' }}>
                  <label style={{ flex: '1 1 200px' }}>
                    <div className="muted">Title *</div>
                    <input value={s.title} required onChange={e => setSection(idx, { title: e.target.value })}
                           placeholder="Part A" />
                  </label>
                  <label style={{ flex: '1 1 200px' }}>
                    <div className="muted">Question type *</div>
                    <select value={s.questionType} onChange={e => setSection(idx, { questionType: e.target.value as QuestionType })}>
                      {QTYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
                    </select>
                  </label>
                </div>
                <div className="row" style={{ gap: 12, flexWrap: 'wrap', marginTop: 8 }}>
                  <label style={{ flex: '1 1 120px' }}>
                    <div className="muted"># Questions *</div>
                    <input type="number" min={1} value={s.questionCount}
                           onChange={e => setSection(idx, { questionCount: parseInt(e.target.value) || 0 })} />
                  </label>
                  <label style={{ flex: '1 1 140px' }}>
                    <div className="muted">Answer (0 = all)</div>
                    <input type="number" min={0} value={s.requiredAnswers}
                           onChange={e => setSection(idx, { requiredAnswers: parseInt(e.target.value) || 0 })} />
                  </label>
                  <label style={{ flex: '1 1 120px' }}>
                    <div className="muted">Marks each *</div>
                    <input type="number" min={1} value={s.marksPerQuestion}
                           onChange={e => setSection(idx, { marksPerQuestion: parseInt(e.target.value) || 0 })} />
                  </label>
                  <div style={{ flex: '0 0 auto', alignSelf: 'flex-end' }}>
                    <span className="muted">= {sectionMarks(s)} marks</span>
                  </div>
                </div>
                <label style={{ display: 'block', marginTop: 8 }}>
                  <div className="muted">Instructions (optional)</div>
                  <input value={s.instructions ?? ''} onChange={e => setSection(idx, { instructions: e.target.value })}
                         placeholder="Answer all questions." />
                </label>
                <label style={{ display: 'flex', gap: 8, marginTop: 10, alignItems: 'center', cursor: 'pointer' }}>
                  <input type="checkbox" checked={s.isEitherOr}
                         style={{ width: 'auto', margin: 0, flex: '0 0 auto' }}
                         onChange={e => setSection(idx, { isEitherOr: e.target.checked })} />
                  <span>Either/or — each question is an "answer (a) OR (b)" pair</span>
                </label>
                {pSections.length > 1 && (
                  <button type="button" className="link-btn" style={{ color: 'var(--danger)', marginTop: 8 }}
                          onClick={() => setPSections(rows => rows.filter((_, i) => i !== idx))}>
                    Remove section
                  </button>
                )}
              </div>
            ))}
            <button type="button" style={{ marginTop: 8, alignSelf: 'flex-start' }}
                    onClick={() => setPSections(rows => [...rows, emptySection()])}>
              + Add section
            </button>

            <div className="muted" style={{ marginTop: 8 }}>Total: <strong>{pTotal} marks</strong></div>
            {pError && <div className="error">{pError}</div>}
            <div className="modal-actions">
              <button type="button" onClick={() => setShowPattern(false)}>Cancel</button>
              <button type="submit" className="primary" disabled={pBusy}>
                {pBusy ? 'Saving…' : editPatternId ? 'Save changes' : 'Create pattern'}
              </button>
            </div>
          </form>
        </Modal>
      )}

      {/* ---- Generated paper preview ---- */}
      {preview && (
        <Modal title="Generated paper — preview" onClose={() => setPreview(null)}>
          <div className="col" style={{ marginTop: 12 }}>
            <h2 style={{ margin: 0 }}>{preview.paper.title}</h2>
            <div className="muted">
              {preview.paper.totalMarks} marks · {preview.paper.durationMinutes} minutes
            </div>
            {preview.paper.instructions && (
              <p style={{ marginTop: 8 }}>{preview.paper.instructions}</p>
            )}

            {preview.paper.sections.map((sec, si) => (
              <div key={si} className="card" style={{ padding: 12, marginTop: 12 }}>
                <div style={{ fontWeight: 600 }}>{sec.title}</div>
                <div className="muted" style={{ fontSize: 13 }}>
                  {qtypeLabel(sec.questionType)}
                  {sec.requiredAnswers > 0 ? ` · answer any ${sec.requiredAnswers}` : ' · answer all'}
                </div>
                {sec.instructions && <div className="muted" style={{ marginTop: 4 }}>{sec.instructions}</div>}
                <ol style={{ marginTop: 8, paddingLeft: 20 }}>
                  {sec.questions.map((q, qi) => {
                    const opts = (o: { text: string; isCorrect: boolean }[] | null) =>
                      o && o.length > 0 ? (
                        <ul style={{ marginTop: 4, listStyle: 'none', paddingLeft: 0 }}>
                          {o.map((x, oi) => (
                            <li key={oi} style={{ color: x.isCorrect ? 'var(--success, green)' : undefined }}>
                              {x.isCorrect ? '✓ ' : '○ '}{x.text}
                            </li>
                          ))}
                        </ul>
                      ) : null;
                    const eitherOr = sec.isEitherOr && !!q.alternativePrompt;
                    return (
                      <li key={qi} style={{ marginBottom: 10 }}>
                        <span className="muted" style={{ float: 'right' }}>
                          ({q.marks} mark{q.marks === 1 ? '' : 's'})
                        </span>
                        {eitherOr ? (
                          <>
                            <div><strong>(a)</strong> {q.prompt}</div>
                            {opts(q.options)}
                            <div className="muted" style={{ fontWeight: 600, margin: '4px 0' }}>OR</div>
                            <div><strong>(b)</strong> {q.alternativePrompt}</div>
                            {opts(q.alternativeOptions)}
                          </>
                        ) : (
                          <>
                            <div>{q.prompt}</div>
                            {opts(q.options)}
                          </>
                        )}
                      </li>
                    );
                  })}
                </ol>
              </div>
            ))}

            <div className="modal-actions">
              <button type="button" onClick={() => setPreview(null)}>Close</button>
              <button type="button" className="primary" disabled={accepting} onClick={accept}>
                {accepting ? 'Creating…' : 'Accept into draft exam'}
              </button>
            </div>
          </div>
        </Modal>
      )}
    </OrgLayout>
  );
}
